import React, { useRef } from 'react';
import { HiOutlineSparkles } from "react-icons/hi2";
import { FaRegImage } from "react-icons/fa";
import { IoIosCloseCircle } from "react-icons/io";
import { HiChevronUpDown } from "react-icons/hi2";
import { Menu } from '@headlessui/react';
import { LuRectangleVertical, LuRectangleHorizontal } from "react-icons/lu";
import { BiRectangle } from "react-icons/bi";
import VideoStyleSelector from './VideoStyleSelector';
import VideoEffectsSelector from './VideoEffectsSelector';

const VideoTaskbar = ({ 
  inputValue, 
  setInputValue, 
  selectedStyle, 
  setSelectedStyle,
  selectedModel,
  setSelectedModel,
  selectedDuration,
  setSelectedDuration,
  isImageMode,
  setIsImageMode,
  uploadedImage,
  setUploadedImage,
  imageUrl,
  setImageUrl,
  selectedEffect,
  setSelectedEffect,
  handleCreate, 
  isLoading,
  aspectRatio,
  setAspectRatio
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = React.useState(false);
  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const dropdownRef = useRef(null);
  const durationDropdownRef = useRef(null);

  const aspectRatios = [
    { value: "1:1", icon: BiRectangle, label: "1:1" },
    { value: "16:9", icon: LuRectangleHorizontal, label: "16:9" },
    { value: "9:16", icon: LuRectangleVertical, label: "9:16" },
  ];

  const selectedAspectRatio = aspectRatios.find(ratio => ratio.value === aspectRatio);

  // Cleanup preview URL when component unmounts
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const handleImageUpload = (e) => {
    e.preventDefault();
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files && files[0]) {
      // Clean up previous preview URL if it exists
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      setUploadedImage(files[0]);
      setIsImageMode(true);
      
      // Create new preview URL
      const objectURL = URL.createObjectURL(files[0]);
      setPreviewUrl(objectURL);
      setImageUrl(null); // Don't set the blob URL as the image_url
    }
    setIsDragging(false);
  };

  const handleRemoveImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setUploadedImage(null);
    setImageUrl(null);
    setIsImageMode(false);
    setSelectedStyle("realistic");
    setSelectedEffect(null);
    
    // Reset any file input elements
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const calculateCost = () => {
    const baseCost = selectedModel === "Standard" ? 100 : 150;
    const durationMultiplier = selectedDuration === 5 ? 1 : 2;
    return baseCost * durationMultiplier;
  };

  return (
    <div className="fixed bottom-6 left-[240px] right-6 z-50">
      <div className="mx-auto bg-white/90 backdrop-blur-lg border border-gray-200/50 rounded-2xl shadow-lg p-3" style={{ maxWidth: '800px' }}>
        <div className="flex flex-col gap-3">
          {/* Path Selection */}
          <div className="flex items-center gap-3 px-2">
            <button
              onClick={() => {
                setIsImageMode(false);
                setSelectedEffect(null);
                handleRemoveImage();
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                !isImageMode && !selectedEffect
                  ? 'bg-blue-50 text-blue-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <HiOutlineSparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Text-to-Video</span>
            </button>
            <button
              onClick={() => setIsImageMode(true)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isImageMode || selectedEffect
                  ? 'bg-purple-50 text-purple-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FaRegImage className="w-4 h-4" />
              <span className="text-sm font-medium">Image-to-Video</span>
            </button>
          </div>

          {/* Input and Generate Button */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isLoading && inputValue.trim()) {
                    e.preventDefault();
                    handleCreate();
                  }
                }}
                className={`w-full h-10 px-4 text-black text-sm focus:outline-none border border-gray-200/50 rounded-full bg-transparent ${
                  selectedEffect ? 'cursor-not-allowed' : ''
                }`}
                placeholder="A majestic dragon soaring over ancient ruins at sunset"
                disabled={selectedEffect !== null}
              />
              {isImageMode && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                    <FaRegImage className="h-5 w-5 text-black" />
                  </label>
                </div>
              )}
            </div>
            <button
              onClick={handleCreate}
              className="bg-black text-white font-medium text-sm py-2 px-4 rounded-full shadow-sm hover:bg-gray-900 transition-all duration-300 flex items-center justify-center gap-3 min-w-[120px] whitespace-nowrap"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="grid min-h-[12px] w-full place-items-center">
                  <svg
                    className="text-gray-300 animate-spin"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                  >
                    <path
                      d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    ></path>
                    <path
                      d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-gray-900"
                    ></path>
                  </svg>
                </div>
              ) : (
                <>
                  <span>Create</span>
                  <div className="w-[1px] h-4 bg-white/20"></div>
                  <span className="text-xs opacity-70">{calculateCost()}</span>
                </>
              )}
            </button>
          </div>

          {/* Preview and Controls */}
          <div className="flex items-center gap-3 px-2">
            {/* Image Preview */}
            {(uploadedImage || imageUrl) && (
              <div className="relative">
                <img
                  src={imageUrl || previewUrl}
                  className="w-10 h-10 rounded-lg object-cover"
                  alt="Preview"
                />
                <button
                  onClick={handleRemoveImage}
                  className="absolute -top-1 -right-1 text-black bg-white rounded-full"
                >
                  <IoIosCloseCircle size={16} />
                </button>
              </div>
            )}

            {/* Style Selector (only show if not using effects and not in image mode) */}
            {!selectedEffect && !isImageMode && (
              <VideoStyleSelector
                selectedStyle={selectedStyle}
                setSelectedStyle={setSelectedStyle}
              />
            )}

            {/* Effects Selector (only show in image mode) */}
            {isImageMode && (
              <>
                <div className="h-6 w-[1px] bg-gray-200"></div>
                <VideoEffectsSelector
                  selectedEffect={selectedEffect}
                  setSelectedEffect={(effect) => {
                    if (effect === selectedEffect) {
                      setSelectedEffect(null);
                      setSelectedModel("Standard");
                    } else {
                      setSelectedEffect(effect);
                      setSelectedModel("Advanced");
                    }
                  }}
                />
              </>
            )}

            {/* Model and Duration Dropdowns */}
            <div className="h-6 w-[1px] bg-gray-200"></div>
            <Menu as="div" className="relative">
              <Menu.Button
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                ref={dropdownRef}
              >
                {selectedModel}
                <HiChevronUpDown className="w-4 h-4" />
              </Menu.Button>
              <Menu.Items className="absolute bottom-full mb-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200/50 overflow-hidden focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        setSelectedModel("Standard");
                        setIsModelDropdownOpen(false);
                      }}
                      className={`${
                        active ? 'bg-gray-50' : ''
                      } ${
                        selectedModel === "Standard" ? 'text-blue-600' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-3 text-sm font-medium`}
                    >
                      Standard
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        setSelectedModel("Advanced");
                        setIsModelDropdownOpen(false);
                      }}
                      className={`${
                        active ? 'bg-gray-50' : ''
                      } ${
                        selectedModel === "Advanced" ? 'text-blue-600' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-3 text-sm font-medium`}
                    >
                      Advanced
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Menu>

            <Menu as="div" className="relative">
              <Menu.Button
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setIsDurationDropdownOpen(!isDurationDropdownOpen)}
                ref={durationDropdownRef}
              >
                {selectedDuration}s
                <HiChevronUpDown className="w-4 h-4" />
              </Menu.Button>
              <Menu.Items className="absolute bottom-full mb-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200/50 overflow-hidden focus:outline-none">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        setSelectedDuration(5);
                        setIsDurationDropdownOpen(false);
                      }}
                      className={`${
                        active ? 'bg-gray-50' : ''
                      } ${
                        selectedDuration === 5 ? 'text-blue-600' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-3 text-sm font-medium`}
                    >
                      5 seconds
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => {
                        setSelectedDuration(10);
                        setIsDurationDropdownOpen(false);
                      }}
                      className={`${
                        active ? 'bg-gray-50' : ''
                      } ${
                        selectedDuration === 10 ? 'text-blue-600' : 'text-gray-700'
                      } group flex w-full items-center px-4 py-3 text-sm font-medium`}
                    >
                      10 seconds
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Menu>

            {/* Aspect Ratio (only show in text-to-video mode) */}
            {!isImageMode && (
              <>
                <div className="h-6 w-[1px] bg-gray-200"></div>
                <Menu as="div" className="relative">
                  <Menu.Button className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                    {selectedAspectRatio && (
                      <>
                        {React.createElement(selectedAspectRatio.icon, {
                          className: "w-4 h-4",
                        })}
                        {selectedAspectRatio.label}
                      </>
                    )}
                    <HiChevronUpDown className="w-4 h-4" />
                  </Menu.Button>
                  <Menu.Items className="absolute bottom-full mb-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200/50 overflow-hidden focus:outline-none">
                    {aspectRatios.map((ratio) => (
                      <Menu.Item key={ratio.value}>
                        {({ active }) => (
                          <button
                            onClick={() => setAspectRatio(ratio.value)}
                            className={`${
                              active ? 'bg-gray-50' : ''
                            } ${
                              aspectRatio === ratio.value ? 'text-blue-600' : 'text-gray-700'
                            } group flex w-full items-center gap-3 px-4 py-3 text-sm font-medium`}
                          >
                            {React.createElement(ratio.icon, {
                              className: "w-4 h-4",
                            })}
                            {ratio.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Menu>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoTaskbar; 